import {
    message, Space, Tooltip, Upload,
} from 'antd';
import {RcFile, UploadProps} from 'antd/es/upload/interface';
import {get} from 'lodash-es';
import React, {FC} from 'react';

import {IFieldMeta} from 'entities/metadata';
import {resetLoadedData} from 'modules/data';
import {ReactComponent as PaperClip} from 'shared/assets/clip.svg';
import {ReactComponent as DownloadIcon} from 'shared/assets/download.svg';
import {ReactComponent as DeleteIcon} from 'shared/assets/forms/delete-x.svg';
import {EEntityType} from 'shared/constants/entities';
import {ECommonMessages} from 'shared/constants/messages';
import {convertToFormData, downloadFile, showMessageFromResponse} from 'shared/utils';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {
    fileUploadCellActions,
    selectIsCellFileUploading,
} from 'store/slices/file-upload-cell-slice/file-upload-cell';

import {deleteAttachment, uploadAttachment} from './utils/file-upload.utils';

import './file-upload-cell.less';

export interface IFileDownloadCellProps {
    entityName: string;
    value: any;
    fieldMeta: IFieldMeta;
    record: Record<string, any>;
    isSignUpload: boolean;
}

export const FileUploadCell: FC<IFileDownloadCellProps> = ({
    value,
    record,
    entityName,
    fieldMeta,
    isSignUpload,
}: IFileDownloadCellProps) => {
    const dispatch = useAppDispatch();
    const {
        accept,
        dependentsFieldKey: attachmentPath,
        referenceUrl,
    } = fieldMeta;
    const isCellFileUploading = useAppSelector(selectIsCellFileUploading);

    const attachment = attachmentPath ? get(record, attachmentPath) : undefined;
    const handleBeforeUpload = (file: RcFile) => {
        const formData = new FormData();
        formData.append('file', file as RcFile);
        return false;
    };

    const handleUploadChange: UploadProps['onChange'] = info => {
        if (info.file && referenceUrl) {
            dispatch(fileUploadCellActions.setIsCellFileUploading(true));
            message.loading({
                content: <span className="ml-1">Загрузка файлов</span>,
                key: entityName,
                duration: 0,
            });
            uploadAttachment(referenceUrl, convertToFormData({
                docId: record?.docId,
                reportRow: isSignUpload ? record?.attachmentId : record?.sourceDocId,
                reportRowId: isSignUpload ? record?.attachmentId : record?.sourceDocId,
                attach: info.file as unknown as File,
            }))
                .then(data => {
                    if (data.status === 200) {
                        dispatch(resetLoadedData(entityName, EEntityType.TABLE));
                        showMessageFromResponse({response: data, isError: false});
                    }
                })
                .catch(error => {
                    showMessageFromResponse({response: error?.response, isError: true});
                })
                .finally(() => {
                    dispatch(fileUploadCellActions.setIsCellFileUploading(false));
                    message.destroy(entityName);
                });
        }
    };

    const handleDelete = () => {
        if (referenceUrl && attachment && !isCellFileUploading) {
            deleteAttachment(`${referenceUrl}/${attachment}`)
                .then(data => {
                    if (data.status === 200) {
                        dispatch(resetLoadedData(entityName, EEntityType.TABLE));
                        showMessageFromResponse({response: data, isError: false});
                    }
                })
                .catch(error => {
                    showMessageFromResponse({response: error?.response, isError: true});
                });
        }
    };

    const handleDownload = () => {
        const filename = value;
        const downloadPath = isSignUpload ? 'signature' : 'attachment';
        if (filename && attachment) {
            downloadFile(`${entityName}/${downloadPath}/${attachment}`, filename)
                .then(data => {
                    if (data.status === 200) {
                        showMessageFromResponse({
                            response: data,
                            isError: false,
                            customMessage: ECommonMessages.UPLOAD_FILE_SUCCESS,
                        });
                    }
                })
                .catch(error => {
                    showMessageFromResponse({response: error?.response, isError: true});
                });
        }
    };

    return (
        <div className="file-upload-cell">
            {value ? (
                <div className="action">
                    <Tooltip
                        title={value}
                    >
                        <Space size={4}>
                            <DeleteIcon
                                onClick={handleDelete}
                            />
                            <PaperClip onClick={handleDownload} />
                        </Space>
                    </Tooltip>
                </div>
            ) : (
                <Upload
                    onChange={handleUploadChange}
                    beforeUpload={handleBeforeUpload}
                    showUploadList={false}
                    accept={accept}
                    disabled={isCellFileUploading}
                >
                    <div className="action">
                        <DownloadIcon /> Загрузить
                    </div>
                </Upload>
            )}
        </div>
    );
};
